import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

window.addEventListener('load', () => {
  gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

  let ctx;
  let resizeTimeout;
  let lastIsDesktop = window.innerWidth >= 1024;

  const initSystemAnimation = () => {
    if (ctx) ctx.revert();

    const wrapper = document.querySelector('.system__animations-wrapper');
    const mobileContent = document.querySelector('.system__mobile-content');
    const cardsWrapper = document.querySelector('.system__cards');

    if (!wrapper) return;

    const isDesktop = window.innerWidth >= 1024;
    const standardCards = wrapper.querySelectorAll('.system__card');

    if (!isDesktop && mobileContent && cardsWrapper) {
      if (cardsWrapper.parentElement !== mobileContent) {
        cardsWrapper.removeAttribute('style');
        const allInnerElements = cardsWrapper.querySelectorAll('*');
        allInnerElements.forEach((el) => el.removeAttribute('style'));

        mobileContent.appendChild(cardsWrapper);
      }
    } else if (isDesktop && wrapper && cardsWrapper) {
      if (cardsWrapper.parentElement !== wrapper) {
        cardsWrapper.removeAttribute('style');
        const allInnerElements = cardsWrapper.querySelectorAll('*');
        allInnerElements.forEach((el) => el.removeAttribute('style'));

        wrapper.appendChild(cardsWrapper);
      }
    }

    const gridSvg = wrapper.querySelector('.system__grid-svg');
    if (!gridSvg) return;

    ctx = gsap.context(() => {
      const intro = wrapper.querySelector('.system__intro');
      const gridText = wrapper.querySelector('.system__grid-text');

      const orbitSvg = wrapper.querySelector('.system__orbit-svg');
      const orbitText = wrapper.querySelector('.system__orbit-text');

      const sloganCard = wrapper.querySelector('.system__slogan-card');

      const cardPreviews = wrapper.querySelectorAll('.system__card-preview');
      const cardHeadings = wrapper.querySelectorAll('.system__card-heading');

      const cardMain = wrapper.querySelectorAll('.system__card-main-wrap');

      const cardDescs = wrapper.querySelectorAll('.system__card-desc');
      const cardStats = wrapper.querySelectorAll('.system__card-stats');
      const cardSides = wrapper.querySelectorAll('.system__card-side');

      const centralRotor = wrapper.querySelector('.system__central-rotor');
      const track1 = wrapper.querySelector('.system__track-1');
      const point1a = wrapper.querySelector('.system__point-1-a');
      const point1b = wrapper.querySelector('.system__point-1-b');

      const totalStandardCards = standardCards.length;
      const cardSnapWindows = [];

      if (orbitSvg) {
        gsap.set(orbitSvg, {
          transformOrigin: '50% 50%',
        });
      }

      if (!isDesktop && cardsWrapper) {
        gsap.set(cardsWrapper, { clearProps: 'all' });
        if (standardCards.length)
          gsap.set(standardCards, { clearProps: 'all' });
        if (sloganCard) gsap.set(sloganCard, { clearProps: 'all' });
        if (cardPreviews.length) gsap.set(cardPreviews, { clearProps: 'all' });
        if (cardHeadings.length) gsap.set(cardHeadings, { clearProps: 'all' });
        if (cardDescs.length)
          gsap.set([cardDescs, cardStats, cardSides], { clearProps: 'all' });
        if (cardMain.length) gsap.set(cardMain, { clearProps: 'all' });
      }

      const baseTl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: 'top top',
          end: isDesktop ? '+=900%' : '+=200%',
          pin: true,
          scrub: 1,
          invalidateOnRefresh: true,
          snap: isDesktop
            ? {
                delay: 0.01,
                duration: { min: 0.08, max: 0.25 },
                ease: 'power2.out',
                snapTo: (progress, self) => {
                  if (!cardSnapWindows.length) return progress;

                  const duration = baseTl.duration();
                  if (!duration) return progress;

                  const time = progress * duration;
                  const activeSnapWindow = cardSnapWindows.find(
                    ({ start, end }) => time > start && time < end
                  );

                  if (!activeSnapWindow) return progress;

                  const targetTime =
                    self && self.direction < 0
                      ? activeSnapWindow.start
                      : activeSnapWindow.end;

                  return targetTime / duration;
                },
              }
            : false,
        },
      });

      baseTl.set(gridSvg, { opacity: 0, autoAlpha: 0, y: 80 });
      baseTl.set([orbitSvg, orbitText], { opacity: 0, autoAlpha: 0 });

      if (isDesktop && cardsWrapper) {
        baseTl.set(cardsWrapper, { opacity: 0, autoAlpha: 0 });

        if (sloganCard) {
          baseTl.set(sloganCard, { opacity: 0, autoAlpha: 0 });
        }

        if (totalStandardCards > 0) {
          baseTl.set(standardCards, { opacity: 0, y: 40 });
          standardCards.forEach((card, idx) => {
            if (idx === 0) baseTl.set(card, { xPercent: -50 });
            else if (idx === 1) baseTl.set(card, { xPercent: -100 });
            else if (idx === 2) baseTl.set(card, { xPercent: 0 });
            else baseTl.set(card, { xPercent: 50 * (idx - 2) });
          });
        }

        if (cardPreviews.length && cardHeadings.length) {
          baseTl.set(cardPreviews, { gap: '0rem' });
          baseTl.set(cardHeadings, { flex: '0 0 0rem' });
        }
      }

      const INTRO_START = 0;
      const INTRO_EXIT_START = INTRO_START + 2.5;
      const INTRO_EXIT_DURATION = 1.0;

      if (intro) {
        const introLines = intro.querySelectorAll('u');
        const syncIntroAfterPreReveal = () => {
          if (baseTl.time() < INTRO_EXIT_START) return;

          gsap.set(intro, { opacity: 0, autoAlpha: 0, y: -50 });
        };
        const queueIntroSyncAfterPreReveal = () => {
          window.requestAnimationFrame(syncIntroAfterPreReveal);
        };

        if (introLines.length) {
          gsap.set(introLines, { '--custom-line-width': '0%' });
        }

        gsap
          .timeline({
            scrollTrigger: {
              trigger: wrapper,
              start: 'top 65%',
              end: 'top top',
              scrub: 0.6,
              invalidateOnRefresh: true,
              onLeave: syncIntroAfterPreReveal,
              onRefresh: queueIntroSyncAfterPreReveal,
              onUpdate: (self) => {
                if (self.progress === 1) syncIntroAfterPreReveal();
              },
            },
          })
          .fromTo(
            intro,
            { opacity: 0, autoAlpha: 0, y: 50 },
            { opacity: 1, autoAlpha: 1, y: 0, duration: 1.0 },
            0
          )
          .to(
            introLines,
            { '--custom-line-width': '100%', duration: 0.8 },
            0.4
          );

        baseTl.set(intro, { opacity: 1, autoAlpha: 1, y: 0 }, INTRO_START);

        if (introLines.length) {
          baseTl.set(
            introLines,
            { '--custom-line-width': '100%' },
            INTRO_START
          );

          baseTl.to(
            introLines,
            { '--custom-line-width': '100%', duration: 0.8 },
            INTRO_START + 0.6
          );
        }

        baseTl.to(
          intro,
          { opacity: 0, autoAlpha: 0, y: -50, duration: INTRO_EXIT_DURATION },
          INTRO_EXIT_START
        );
      }

      const GRID_START = INTRO_EXIT_START + INTRO_EXIT_DURATION - 0.25;
      baseTl.to(
        gridSvg,
        { opacity: 1, autoAlpha: 1, y: 0, duration: 1.2 },
        GRID_START
      );

      if (gridText) {
        const gridLines = gridText.querySelectorAll('u');

        baseTl.fromTo(
          gridText,
          { opacity: 0, autoAlpha: 0, y: 50 },
          { opacity: 1, autoAlpha: 1, y: 0, duration: 1.0 },
          GRID_START + 0.15
        );

        if (gridLines.length) {
          baseTl.to(
            gridLines,
            { '--custom-line-width': '100%', duration: 0.8 },
            GRID_START + 1.1
          );
        }

        baseTl.to(
          gridText,
          { opacity: 0, autoAlpha: 0, y: -50, duration: 1.0 },
          GRID_START + 3.0
        );
      }

      const GRID_EXIT_START = GRID_START + 4.0;
      const GRID_EXIT_DURATION = 1.2;

      baseTl.to(
        gridSvg,
        { opacity: 0, autoAlpha: 0, y: -50, duration: GRID_EXIT_DURATION },
        GRID_EXIT_START
      );

      if (isDesktop) {
        const ORBIT_START = GRID_EXIT_START + GRID_EXIT_DURATION * 0.85;
        if (orbitSvg) {
          baseTl.to(
            orbitSvg,
            { opacity: 1, autoAlpha: 1, duration: 1.2 },
            ORBIT_START
          );
        }

        if (orbitText) {
          const orbitLines = orbitText.querySelectorAll('u');

          baseTl.fromTo(
            orbitText,
            { opacity: 0, autoAlpha: 0, scale: 0.5 },
            { opacity: 1, autoAlpha: 1, scale: 1, duration: 1.0 },
            ORBIT_START + 0.5
          );

          if (orbitLines.length) {
            baseTl.to(
              orbitLines,
              { '--custom-line-width': '100%', duration: 0.8 },
              ORBIT_START + 1.1
            );
          }
        }

        if (cardsWrapper) {
          baseTl.to(
            cardsWrapper,
            { opacity: 1, autoAlpha: 1, duration: 0.5 },
            ORBIT_START + 2.5
          );

          if (totalStandardCards > 0) {
            standardCards.forEach((card, idx) => {
              const startTime = ORBIT_START + 3.0 + idx * 1.5;

              baseTl.to(card, { opacity: 1, y: 0, duration: 0.8 }, startTime);

              const preview = card.querySelector('.system__card-preview');
              const heading = card.querySelector('.system__card-heading');

              if (preview && heading) {
                baseTl
                  .to(
                    preview,
                    { gap: '0.875rem', duration: 1.2 },
                    startTime + 0.2
                  )
                  .to(
                    heading,
                    {
                      flex: '0 0 clamp(220px, 17.92vw, 16.125rem)',
                      duration: 1.2,
                    },
                    startTime + 0.2
                  );
              }
            });
          }

          const STACK_START =
            ORBIT_START +
            3.0 +
            (totalStandardCards > 0 ? totalStandardCards * 1.5 : 0) +
            1.0;
          const targetsToHide = [];
          if (centralRotor) targetsToHide.push(centralRotor);
          if (track1) targetsToHide.push(track1);
          if (orbitText) targetsToHide.push(orbitText);
          if (point1a) targetsToHide.push(point1a);
          if (point1b) targetsToHide.push(point1b);

          if (targetsToHide.length) {
            baseTl.to(
              targetsToHide,
              { opacity: 0, autoAlpha: 0, duration: 1.5 },
              STACK_START
            );
          }

          if (orbitSvg) {
            baseTl.to(
              orbitSvg,
              { scale: 1.3, duration: 6.0, ease: 'power2.inOut' },
              STACK_START
            );
          }

          if (totalStandardCards > 0) {
            standardCards.forEach((card, idx) => {
              baseTl.set(
                card,
                { zIndex: totalStandardCards + 1 - idx },
                STACK_START
              );
            });

            baseTl.set(
              standardCards,
              {
                '--grad-color-1': 'rgba(255, 255, 255, 0.4)',
                '--grad-color-2': 'rgba(255, 255, 255, 0.4)',
              },
              STACK_START
            );

            baseTl.set(
              [cardDescs, cardStats, cardSides],
              {
                opacity: 0,
                autoAlpha: 0,
              },
              STACK_START
            );

            standardCards.forEach((card, idx) => {
              const currentScale = 1 - idx * 0.06;
              const targetOpacity = idx === 0 ? 1 : 1 - idx * 0.04;

              baseTl.to(
                card,
                {
                  left: '50%',
                  top: '50%',
                  xPercent: -50,
                  yPercent: -50,
                  transformOrigin: 'center bottom',
                  scale: currentScale,
                  keyframes: {
                    '0%': { opacity: 1 },
                    '40%': { opacity: targetOpacity },
                    '100%': { opacity: targetOpacity },
                  },
                  duration: 5.0,
                  ease: 'power3.inOut',
                },
                STACK_START
              );
            });

            baseTl.to(
              standardCards,
              {
                '--grad-color-1': '#F3EFEF',
                '--grad-color-2': '#D8E7F8',
                duration: 5.0,
                ease: 'power3.inOut',
              },
              STACK_START
            );

            const cardHeadingSpans = wrapper.querySelectorAll(
              '.system__card-heading span'
            );

            baseTl.to(
              standardCards,
              {
                width: '74rem',
                height: '38.125rem',
                borderWidth: '4px',
                borderRadius: '30px',
                padding: '1.75rem 2.0625rem 1.625rem',
                duration: 3.0,
                ease: 'power3.inOut',
                onUpdate: function () {
                  if (cardHeadingSpans.length) {
                    if (this.progress() >= 0.5) {
                      cardHeadingSpans.forEach((span) => {
                        span.style.width = '100%';
                      });
                    } else {
                      cardHeadingSpans.forEach((span) => {
                        span.style.width = '';
                      });
                    }
                  }
                },
              },
              STACK_START + 2
            );

            standardCards.forEach((card, idx) => {
              if (idx > 0) {
                baseTl.to(
                  card,
                  {
                    marginTop: `${idx * -60}px`,
                    duration: 3.0,
                    ease: 'power3.inOut',
                  },
                  STACK_START + 2
                );
              }
            });

            baseTl.set(cardHeadings, { flex: '1 1 100%' }, STACK_START + 2);
            baseTl.to(
              cardHeadings,
              {
                fontSize: 'clamp(30px, 2.78vw, 2.5rem)',
                background: 'transparent',
                duration: 3.0,
                ease: 'power3.inOut',
              },
              STACK_START + 2
            );

            if (cardHeadingSpans.length) {
              baseTl.to(
                cardHeadingSpans,
                {
                  padding: '0.6875rem 1.1875rem 0.6875rem',
                  duration: 3.0,
                  ease: 'power3.inOut',
                },
                STACK_START + 2
              );
            }

            baseTl.to(
              cardPreviews,
              {
                padding: '2rem 2.1875rem 1.313rem',
                duration: 3.0,
                ease: 'power3.inOut',
              },
              STACK_START + 2
            );

            if (orbitSvg) {
              baseTl.to(
                orbitSvg,
                {
                  opacity: 0,
                  autoAlpha: 0,
                  duration: 1.0,
                },
                STACK_START + 4.0
              );
            }

            baseTl.set(
              [cardDescs, cardStats, cardSides],
              {
                position: 'relative',
                clearProps: 'pointerEvents',
              },
              STACK_START + 4.9
            );

            if (cardMain.length) {
              baseTl.set(
                cardMain,
                {
                  overflowY: 'auto',
                  overflowX: 'hidden',
                },
                STACK_START + 4.9
              );
            }

            baseTl.to(
              [cardDescs, cardStats, cardSides],
              {
                opacity: 1,
                autoAlpha: 1,
                duration: 1.5,
                ease: 'power2.out',
                onComplete: () => {
                  standardCards.forEach((card) => {
                    const mainElement = card.querySelector(
                      '.system__card-main-wrap'
                    );
                    if (
                      mainElement &&
                      mainElement.scrollHeight > mainElement.clientHeight
                    ) {
                      card.classList.add('has-scroll');
                    }
                  });
                },
              },
              STACK_START + 5.0
            );
          }

          const SLOGAN_START = STACK_START + 5.0;
          const allStackCards = Array.from(standardCards);

          if (sloganCard) {
            allStackCards.push(sloganCard);
            const sloganIdx = allStackCards.length - 1;
            const sloganScale = 1 - sloganIdx * 0.06;
            const sloganMarginTop = sloganIdx * -60;
            const sloganOpacity = 1 - sloganIdx * 0.04;

            baseTl.set(
              sloganCard,
              {
                zIndex: 1,
                left: '50%',
                top: '50%',
                xPercent: -50,
                yPercent: -50,
                transformOrigin: 'center bottom',
                width: '74rem',
                height: '38.125rem',
                borderRadius: '30px',
                scale: sloganScale,
                marginTop: `${sloganMarginTop}px`,
              },
              SLOGAN_START
            );

            baseTl.to(
              sloganCard,
              {
                opacity: sloganOpacity,
                autoAlpha: 1,
                duration: 1.0,
                ease: 'power2.out',
              },
              SLOGAN_START
            );
          }

          const totalCards = allStackCards.length;
          if (totalCards > 0) {
            const CARD_CYCLE_DURATION = 4.0;
            const CARD_CYCLE_INTERVAL = 5.0;
            let currentCycleStart = SLOGAN_START + 2.0;

            for (let i = 0; i < totalCards - 1; i++) {
              cardSnapWindows.push({
                start: currentCycleStart,
                end: currentCycleStart + CARD_CYCLE_DURATION,
              });

              baseTl.to(
                allStackCards[i],
                {
                  keyframes: {
                    '0%': {
                      yPercent: -50,
                      scale: 1,
                      opacity: 1,
                      filter: 'blur(0rem)',
                    },
                    '40%': {
                      yPercent: -47,
                      scale: 1.02,
                      opacity: 0.5,
                      filter: 'blur(0.2rem)',
                    },
                    '99%': {
                      zIndex: totalCards + 1 - i,
                    },
                    '100%': {
                      yPercent: -40,
                      scale: 1.05,
                      opacity: 0,
                      filter: 'blur(0.5rem)',
                      zIndex: 0,
                    },
                  },
                  duration: CARD_CYCLE_DURATION,
                  ease: 'power1.inOut',
                },
                currentCycleStart
              );

              for (let j = i + 1; j < totalCards; j++) {
                const activeIndexInStack = j - (i + 1);
                const nextScale = 1 - activeIndexInStack * 0.06;
                const nextMarginTop = activeIndexInStack * -60;
                const nextOpacity = 1 - activeIndexInStack * 0.04;

                baseTl.to(
                  allStackCards[j],
                  {
                    scale: nextScale,
                    opacity: nextOpacity,
                    marginTop: `${nextMarginTop}px`,
                    duration: CARD_CYCLE_DURATION,
                    ease: 'power1.inOut',
                  },
                  currentCycleStart
                );
              }

              currentCycleStart += CARD_CYCLE_INTERVAL;
            }

            const firstCard = standardCards[0];
            if (firstCard) {
              baseTl.to(
                {},
                {
                  duration: CARD_CYCLE_DURATION,
                  ease: 'power1.inOut',
                  onUpdate: function () {
                    const pinSpacer = wrapper.parentElement;
                    if (
                      pinSpacer &&
                      pinSpacer.classList.contains('pin-spacer')
                    ) {
                      const cardHeight = firstCard.offsetHeight;
                      const formulaValue =
                        (window.innerHeight - cardHeight) / 2;

                      if (!this.initialPadding) {
                        this.initialPadding =
                          parseFloat(
                            window.getComputedStyle(pinSpacer).paddingBottom
                          ) || 0;
                      }

                      const targetPadding = this.initialPadding - formulaValue;
                      const currentPadding = gsap.utils.interpolate(
                        this.initialPadding,
                        targetPadding,
                        this.progress()
                      );

                      pinSpacer.style.paddingBottom = `${currentPadding}px`;
                    }
                  },
                },
                currentCycleStart - 5.0
              );
            }
          }
        }
      }

      const balls = Array.from(
        gridSvg.querySelectorAll('[class*="system__grid-ball-"]')
      );
      const allTracks = Array.from(
        gridSvg.querySelectorAll('[class*="system__grid-track-"]')
      );

      if (allTracks.length && balls.length) {
        const shuffledTracks = allTracks.sort(() => 0.5 - Math.random());
        const baseSpeed = 0.01;

        balls.forEach((ball, index) => {
          const track = shuffledTracks[index % shuffledTracks.length];

          if (ball && track) {
            const trackLength = track.getTotalLength();
            const calculatedDuration =
              trackLength * baseSpeed * (0.85 + Math.random() * 0.3);

            const ballTl = gsap.timeline({ repeat: -1 });

            ballTl.fromTo(
              ball,
              { opacity: 0 },
              {
                motionPath: {
                  path: track,
                  align: track,
                  alignOrigin: [0.5, 0.5],
                  start: 0,
                  end: 1,
                },
                duration: calculatedDuration,
                ease: 'none',
              },
              0
            );

            ballTl.fromTo(
              ball,
              { opacity: 0 },
              {
                opacity: 1,
                duration: calculatedDuration * 0.1,
                ease: 'power1.inOut',
              },
              0
            );

            ballTl.to(
              ball,
              {
                opacity: 0,
                duration: calculatedDuration * 0.1,
                ease: 'power1.inOut',
              },
              calculatedDuration * 0.9
            );

            ballTl.delay(index * 0.6);
          }
        });
      }

      const track1El = document.querySelector('.system__track-1');
      const track2El = document.querySelector('.system__track-2');
      const track3El = document.querySelector('.system__track-3');

      const point1aEl = document.querySelector('.system__point-1-a');
      const point1bEl = document.querySelector('.system__point-1-b');
      const point2aEl = document.querySelector('.system__point-2-a');
      const point2bEl = document.querySelector('.system__point-2-b');
      const point3El = document.querySelector('.system__point-3');

      if (track1El && point1aEl && point1bEl) {
        gsap.timeline({ repeat: -1 }).to(point1aEl, {
          motionPath: {
            path: track1El,
            align: track1El,
            alignOrigin: [0.5, 0.5],
            start: 0,
            end: 1,
          },
          duration: 12,
          ease: 'none',
          overwrite: 'auto',
        });

        gsap.timeline({ repeat: -1 }).to(point1bEl, {
          motionPath: {
            path: track1El,
            align: track1El,
            alignOrigin: [0.5, 0.5],
            start: 0.5,
            end: 1.5,
          },
          duration: 12,
          ease: 'none',
          overwrite: 'auto',
        });
      }

      if (track2El && point2aEl && point2bEl) {
        gsap.timeline({ repeat: -1 }).to(point2aEl, {
          motionPath: {
            path: track2El,
            align: track2El,
            alignOrigin: [0.5, 0.5],
            start: 0,
            end: 1,
          },
          duration: 20,
          ease: 'none',
          overwrite: 'auto',
        });

        gsap.timeline({ repeat: -1 }).to(point2bEl, {
          motionPath: {
            path: track2El,
            align: track2El,
            alignOrigin: [0.5, 0.5],
            start: 0.5,
            end: 1.5,
          },
          duration: 20,
          ease: 'none',
          overwrite: 'auto',
        });
      }

      if (track3El && point3El) {
        gsap.timeline({ repeat: -1 }).to(point3El, {
          motionPath: {
            path: track3El,
            align: track3El,
            alignOrigin: [0.5, 0.5],
          },
          duration: 16,
          ease: 'none',
          overwrite: 'auto',
        });
      }

      ScrollTrigger.refresh();
    }, wrapper);
  };

  initSystemAnimation();

  window.addEventListener('resize', () => {
    const isDesktop = window.innerWidth >= 1024;

    if (isDesktop !== lastIsDesktop) {
      lastIsDesktop = isDesktop;
      const currentScrollY = window.scrollY;

      initSystemAnimation();

      window.requestAnimationFrame(() => {
        window.scrollTo(0, currentScrollY);
      });
    } else {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 300);
    }
  });
});
